import dayjs from 'dayjs';
// Types
import {
  CreateMatchupInput,
  CreateTicketEventInput,
  HostedTicketEventsOrderBy,
  RescheduleTicketEventInput,
  SortDirection,
  StreamCreateInput,
  TicketEventStatus,
  TicketTierDetailsInput,
  TicketVenueDetailsInput,
} from 'api/graphql-global-types';
import { HostedTicketEventFields } from 'api/ticketEvents/types/HostedTicketEventFields';
import { GetTicketVenuePlaceDetails_getPlaceDetails } from 'api/ticketEvents/types/GetTicketVenuePlaceDetails';
import {
  GetTicketEventForEdit_hostedTicketEvent,
  GetTicketEventForEdit_hostedTicketTiers,
} from 'api/ticketEvents/types/GetTicketEventForEdit';
import { BoutDraft } from 'helpers/matchcard';
// Constants
import { TIME_ZONE_OPTIONS, TimeZoneOption } from 'constants/timeZone';
import { EVENT_INVITE } from 'constants/routes';
import { TICKET_SALES_CLOSE_HOURS } from 'constants/ticketEvents';
// Helpers
import {
  formatHashtagInput,
  getStreamSaleInput,
  getStreamScheduleInput,
  REPEAT_STREAM_OPTIONS,
  RepeatStreamOption,
} from 'helpers/streams';
import { calculateAffiliateCommission, roundMoney } from 'helpers/affiliate';
import {
  formatCurrencyString,
  formatCurrencyStringWithoutName,
} from 'helpers/formatCurrencyString';
import { getEnvLink } from 'helpers/routes';
import {
  formatTicketEventTableDate,
  getTicketEventPath,
} from 'helpers/ticketSales';
import { mapMatchupsToBouts } from 'helpers/streamMatchups';
import {
  getCurrentTimeZone,
  readWallClockInZone,
  toWallClockInZone,
} from 'helpers/timeZone';
// Ui
import { StatusVariant } from 'ui3/Status/Status';

export const CREATE_EVENT_STEP_LABELS = [
  'Event Details',
  'Pricing & Tickets',
  'Advanced Options',
];

/** The wizard steps Edit Event can open on. */
export const EDIT_EVENT_STEPS = {
  details: 1,
  tickets: 2,
  matchcard: 3,
} as const;

export type EditEventStep = typeof EDIT_EVENT_STEPS[keyof typeof EDIT_EVENT_STEPS];

export const REPEAT_EVENT_OPTIONS = REPEAT_STREAM_OPTIONS.filter(
  (option) => option.value
);

export type EventVenueDraft = {
  name: string;
  addressLine1: string;
  city: string;
  region: string;
  postalCode: string;
  /** ISO 3166-1 alpha-2 */
  country: string;
  latitude: number | null;
  longitude: number | null;
  /** IANA zone of the venue; unknown until a place lookup returns it. */
  timezone: string | null;
};

export type EventTicketTierDraft = {
  /** Client-side key only. */
  id: string;
  label: string;
  price: number;
  capacity: number;
  /** Already on the event, which can't drop a tier. */
  isSaved?: boolean;
};

export type EventFormInputs = {
  name: string;
  description: string;
  hashtags: string[];
  image: File | null;
  previewImage: string;
  isRecurring: boolean;
  repeatsEveryEnum: RepeatStreamOption;
  startDate: Date | '';
  startTime: Date | '';
  /** startDate and startTime combined, set once both are picked. */
  scheduleDate: Date | string;
  timeZone: TimeZoneOption;
  /** Hours after the start that ticket sales close; null keeps the admin's default. */
  salesCloseHours: number | null;
  venue: EventVenueDraft;
  venueSearch: string;
  streamEnabled: boolean;
  price: string;
  isFree: boolean;
  /** Only read while the event streams; any other event sells tickets. */
  ticketsEnabled: boolean;
  tiers: EventTicketTierDraft[];
  affiliateEnabled: boolean;
  affiliateCommissionPercent: string;
  matchcardEnabled: boolean;
  bouts: BoutDraft[];
  fanPollingEnabled: boolean;
  sharePickGraphicsEnabled: boolean;
};

export const EMPTY_EVENT_VENUE: EventVenueDraft = {
  name: '',
  addressLine1: '',
  city: '',
  region: '',
  postalCode: '',
  country: '',
  latitude: null,
  longitude: null,
  timezone: null,
};

export const EVENT_VENUE_FIELDS = Object.keys(
  EMPTY_EVENT_VENUE
) as (keyof EventVenueDraft)[];

export const VENUE_ADDRESS_FIELDS: (keyof EventVenueDraft)[] = [
  'name',
  'addressLine1',
  'city',
  'region',
  'postalCode',
  'country',
];

/** The venue fields a wizard step validates, as react-hook-form paths. */
export const VENUE_STEP_FIELDS = [
  ...VENUE_ADDRESS_FIELDS.map((field) => `venue.${field}`),
  'venue.latitude',
];

export const getEventFormDefaultValues = (
  timeZone: TimeZoneOption
): EventFormInputs => ({
  name: '',
  description: '',
  hashtags: [],
  image: null,
  previewImage: '',
  isRecurring: false,
  repeatsEveryEnum: REPEAT_EVENT_OPTIONS[0],
  startDate: '',
  startTime: '',
  scheduleDate: '',
  timeZone,
  salesCloseHours: null,
  venue: EMPTY_EVENT_VENUE,
  venueSearch: '',
  streamEnabled: false,
  price: '',
  isFree: false,
  ticketsEnabled: true,
  tiers: [],
  affiliateEnabled: false,
  affiliateCommissionPercent: '',
  matchcardEnabled: false,
  bouts: [],
  fanPollingEnabled: false,
  sharePickGraphicsEnabled: false,
});

/** Fields each wizard step validates before moving on, in step order. */
export const CREATE_EVENT_STEP_FIELDS: string[][] = [
  [
    'name',
    'description',
    'previewImage',
    'startDate',
    'startTime',
    'scheduleDate',
    'timeZone',
    'repeatsEveryEnum',
    ...VENUE_STEP_FIELDS,
  ],
  ['price', 'tiers', 'affiliateCommissionPercent'],
  [],
];

/** Only an event that streams can leave tickets off. */
export const eventSellsTickets = (
  values: Pick<EventFormInputs, 'streamEnabled' | 'ticketsEnabled'>
): boolean => !values.streamEnabled || values.ticketsEnabled;

/** An event on a stream runs the stream's affiliate program, which a free stream can't have. */
export const isEventAffiliateAvailable = (
  values: Pick<EventFormInputs, 'streamEnabled' | 'isFree'>
): boolean => !(values.streamEnabled && values.isFree);

type EventAffiliateValues = Pick<
  EventFormInputs,
  'streamEnabled' | 'isFree' | 'affiliateEnabled' | 'affiliateCommissionPercent'
>;

export const getEventAffiliateInput = (
  values: EventAffiliateValues
): { affiliateEnabled: boolean; affiliateCommissionPercent: number | null } => {
  const affiliateEnabled =
    values.affiliateEnabled && isEventAffiliateAvailable(values);

  return {
    affiliateEnabled,
    affiliateCommissionPercent: affiliateEnabled
      ? Number(values.affiliateCommissionPercent)
      : null,
  };
};

export const combineEventDateAndTime = (
  date: Date | '',
  time: Date | ''
): Date | '' =>
  date && time
    ? dayjs(date)
        .hour(dayjs(time).hour())
        .minute(dayjs(time).minute())
        .second(0)
        .millisecond(0)
        .toDate()
    : '';

/** The UTC instant of a date and time picked as wall-clock time in the given zone. */
export const getEventStartsAt = (
  scheduleDate: Date | string,
  tzName: string
): string => readWallClockInZone(scheduleDate, tzName).toISOString();

export type SalesCloseOption = {
  value: number;
  label: string;
};

const formatHours = (hours: number) =>
  `${hours} ${hours === 1 ? 'hour' : 'hours'}`;

export const getSalesCloseLabel = (hours: number): string => {
  if (hours === 0) {
    return 'At start time';
  }

  return hours > 0
    ? `${formatHours(hours)} after start`
    : `${formatHours(-hours)} before start`;
};

export const secondsToHours = (seconds: number): number =>
  Math.round((seconds / 3600) * 100) / 100;

/** The listed choices up to the admin's latest close, plus their default and the event's own close. */
export const getSalesCloseOptions = ({
  defaultHours,
  maxHours,
  currentHours,
}: {
  defaultHours: number;
  maxHours: number;
  currentHours: number | null;
}): SalesCloseOption[] =>
  Array.from(
    new Set([
      ...TICKET_SALES_CLOSE_HOURS.filter((hours) => hours <= maxHours),
      defaultHours,
      ...(currentHours === null ? [] : [currentHours]),
    ])
  )
    .sort((a, b) => a - b)
    .map((hours) => ({
      value: hours,
      label:
        hours === defaultHours
          ? `${getSalesCloseLabel(hours)} (default)`
          : getSalesCloseLabel(hours),
    }));

export const getTicketSalesCloseAt = (
  startsAt: string,
  hours: number
): string =>
  dayjs(startsAt)
    .add(Math.round(hours * 60), 'minute')
    .toISOString();

/** Hours from the start to the sales close, to the minute. */
export const getSalesCloseHours = (
  startsAt: string,
  salesCloseAt: string
): number => secondsToHours(dayjs(salesCloseAt).diff(startsAt, 'second'));

export const getTicketTiersSummary = (
  tiers: Pick<EventTicketTierDraft, 'price' | 'capacity'>[]
): { count: number; ticketsAvailable: number; maxRevenue: number } => ({
  count: tiers.length,
  ticketsAvailable: tiers.reduce((sum, tier) => sum + tier.capacity, 0),
  maxRevenue: tiers.reduce((sum, tier) => sum + tier.price * tier.capacity, 0),
});

/** "$2.50", or "$2.50–$5.00" across amounts that differ. */
export const formatMoneyRange = (amounts: number[]): string => {
  const min = Math.min(...amounts);
  const max = Math.max(...amounts);

  return min === max
    ? formatCurrencyStringWithoutName(min)
    : `${formatCurrencyStringWithoutName(
        min
      )}–${formatCurrencyStringWithoutName(max)}`;
};

/**
 * What one sale brings the organizer before commission: the PPV net of an event that streams (its tickets are left
 * out, as on a stream), else the price of each ticket tier. Empty while nothing is priced.
 */
export const getEventSaleAmounts = (
  values: Pick<EventFormInputs, 'streamEnabled' | 'isFree' | 'tiers'>,
  ppvNetProfit: number
): number[] => {
  if (values.streamEnabled) {
    return !values.isFree && ppvNetProfit > 0 ? [ppvNetProfit] : [];
  }

  return values.tiers.map(({ price }) => price);
};

export type AffiliateSaleSplit = {
  /** Null without a commission or anything priced. */
  participantEarns: string | null;
  /** Null while nothing is priced. */
  totalPerSale: string | null;
};

/** How one sale splits between the participant and the organizer, for the wizard's summary. */
export const getAffiliateSaleSplit = (
  saleAmounts: number[],
  commissionPercent: number
): AffiliateSaleSplit => {
  if (!saleAmounts.length) {
    return { participantEarns: null, totalPerSale: null };
  }

  if (!(commissionPercent > 0)) {
    return {
      participantEarns: null,
      totalPerSale: formatMoneyRange(saleAmounts),
    };
  }

  const commissions = saleAmounts.map((amount) =>
    calculateAffiliateCommission(amount, commissionPercent)
  );
  const kept = saleAmounts.map((amount, index) =>
    roundMoney(amount - commissions[index])
  );

  return {
    participantEarns: `${formatMoneyRange(commissions)} / sale`,
    totalPerSale: `${formatMoneyRange(kept)} / affiliate sale`,
  };
};

export type AffiliateEarning = { amount: string; per: string };

/**
 * A participant's commission on each kind of sale an event makes. PPV shows once it has a price, or on its own while
 * the event sells no tickets.
 */
export const getEventAffiliateEarnings = ({
  ppvNetProfit,
  ticketPrices,
  commissionPercent,
}: {
  ppvNetProfit: number;
  /** Null when the event sells no tickets. */
  ticketPrices: number[] | null;
  commissionPercent: number;
}): AffiliateEarning[] => {
  const earn = (amount: number) =>
    calculateAffiliateCommission(amount, commissionPercent);
  const earnings: AffiliateEarning[] = [];

  if (ppvNetProfit > 0 || ticketPrices === null) {
    earnings.push({
      amount: `${formatMoneyRange([earn(ppvNetProfit)])} USD`,
      per: 'PPV sale',
    });
  }

  if (ticketPrices !== null) {
    earnings.push({
      amount: `${formatMoneyRange(
        ticketPrices.length ? ticketPrices.map(earn) : [0]
      )} USD`,
      per: 'ticket sale',
    });
  }

  return earnings;
};

export const getVenueFromPlace = (
  place: GetTicketVenuePlaceDetails_getPlaceDetails
): EventVenueDraft => ({
  name: place.name || place.formattedAddress.split(',')[0],
  addressLine1: place.addressLine1 || '',
  city: place.city || '',
  region: place.stateCode || place.state || '',
  postalCode: place.postalCode || '',
  country: place.countryCode || '',
  latitude: place.location.latitude,
  longitude: place.location.longitude,
  timezone: place.timezone || null,
});

/** What Google can find the venue by, from the details a host typed in. */
export const getVenueSearchQuery = (venue: EventVenueDraft): string =>
  VENUE_ADDRESS_FIELDS.map((field) => venue[field])
    .filter(Boolean)
    .join(', ');

export const getVenueMapUrl = (latitude: number, longitude: number): string =>
  `https://maps.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`;

/** The picker zone that keeps the venue's clock all year, else the venue's own zone, so the start reads in venue time. */
export const findVenueTimeZoneOption = (
  timezone: string,
  at: Date | string
): TimeZoneOption => {
  const date = at ? dayjs(at) : dayjs();
  const venueTime = date.tz(timezone);
  // January and July tell a zone without daylight saving (Arizona, Saskatchewan) from one with it.
  const seasons = [date.month(0).date(1), date.month(6).date(1)];
  const hasVenueClock = (tzName: string) =>
    seasons.every(
      (day) => day.tz(tzName).utcOffset() === day.tz(timezone).utcOffset()
    );

  return (
    TIME_ZONE_OPTIONS.find((option) => hasVenueClock(option.tzName)) || {
      tzCode: timezone,
      tzName: timezone,
      label: `${timezone} (GMT${venueTime.format('Z')})`,
    }
  );
};

export const buildTicketVenueInput = (
  venue: EventVenueDraft,
  fallbackTimezone: string
): TicketVenueDetailsInput => ({
  name: venue.name.trim(),
  addressLine1: venue.addressLine1.trim(),
  city: venue.city.trim(),
  region: venue.region.trim(),
  postalCode: venue.postalCode.trim(),
  country: venue.country,
  latitude: Number(venue.latitude),
  longitude: Number(venue.longitude),
  timezone: venue.timezone || fallbackTimezone,
});

/** Streams only know the listed zones, so a venue elsewhere gets the same start in the host's listed zone. */
export const getEventStreamScheduleInput = (
  scheduleDate: Date | string,
  timeZone: TimeZoneOption
): ReturnType<typeof getStreamScheduleInput> => {
  if (TIME_ZONE_OPTIONS.some(({ tzCode }) => tzCode === timeZone.tzCode)) {
    return getStreamScheduleInput(scheduleDate, timeZone);
  }

  const streamTimeZone = getCurrentTimeZone();
  const startsAt = getEventStartsAt(scheduleDate, timeZone.tzName);

  return getStreamScheduleInput(
    toWallClockInZone(startsAt, streamTimeZone.tzName),
    streamTimeZone
  );
};

export const buildEventStreamInput = (
  values: EventFormInputs,
  matchups: CreateMatchupInput[]
): StreamCreateInput => {
  const name = values.name.trim();

  return {
    name,
    description: values.description,
    repeatingTitle: values.isRecurring ? name : null,
    repeatsEveryEnum: values.isRecurring ? values.repeatsEveryEnum.value : null,
    ...(values.image && { image: values.image }),
    ...getEventStreamScheduleInput(values.scheduleDate, values.timeZone),
    isHidden: false,
    hashtagInputs: formatHashtagInput(values.hashtags) ?? [],
    // The stream's affiliate program also pays on the event's tickets.
    ...getStreamSaleInput(values, matchups),
  };
};

/** The venue tickets a paid stream sells alongside it, as the Create Stream wizard collects them. */
export type StreamTicketsDraft = {
  ticketsEnabled: boolean;
  venue: EventVenueDraft;
  venueSearch: string;
  doorsOpenTime: Date | '';
  /** Hours after the start that ticket sales close; null keeps the admin's default. */
  salesCloseHours: number | null;
  tiers: EventTicketTierDraft[];
};

export const STREAM_TICKETS_DEFAULT_VALUES: StreamTicketsDraft = {
  ticketsEnabled: false,
  venue: EMPTY_EVENT_VENUE,
  venueSearch: '',
  doorsOpenTime: '',
  salesCloseHours: null,
  tiers: [],
};

type TicketEventBaseValues = {
  name: string | null;
  description: string;
  hashtags?: string[];
  scheduleDate: Date | string;
  timeZone: TimeZoneOption;
  venue: EventVenueDraft;
  salesCloseHours: number | null;
};

/** What every new ticket event sends, whether made on its own or with a stream. */
const getTicketEventBaseInput = (
  values: TicketEventBaseValues
): Pick<
  CreateTicketEventInput,
  | 'name'
  | 'description'
  | 'hashtagInputs'
  | 'venue'
  | 'startsAt'
  | 'salesCloseAt'
> => {
  const { tzName } = values.timeZone;
  const startsAt = getEventStartsAt(values.scheduleDate, tzName);

  return {
    name: (values.name || '').trim(),
    description: values.description,
    hashtagInputs: formatHashtagInput(values.hashtags) ?? [],
    venue: buildTicketVenueInput(values.venue, tzName),
    startsAt,
    ...(values.salesCloseHours !== null && {
      salesCloseAt: getTicketSalesCloseAt(startsAt, values.salesCloseHours),
    }),
  };
};

const toTicketTierInputs = (
  tiers: EventTicketTierDraft[]
): TicketTierDetailsInput[] =>
  tiers.map(({ label, price, capacity }) => ({ label, price, capacity }));

type StreamTicketEventValues = StreamTicketsDraft & TicketEventBaseValues;

/** The ticket event of a new stream: same name and start as the stream, doors on its day. */
export const buildStreamTicketEventInput = (
  values: StreamTicketEventValues,
  streamId: string
): CreateTicketEventInput => {
  const doorsOpen = combineEventDateAndTime(
    dayjs(values.scheduleDate).toDate(),
    values.doorsOpenTime
  );

  return {
    streamId,
    ...getTicketEventBaseInput(values),
    ...(doorsOpen && {
      doorsOpenAt: getEventStartsAt(doorsOpen, values.timeZone.tzName),
    }),
    ticketsEnabled: true,
    tiers: toTicketTierInputs(values.tiers),
  };
};

/** A standalone event's own affiliate program and fan polling; an event on a stream runs the stream's. */
export const getStandaloneEventEngagementInput = (
  values: EventAffiliateValues & Pick<EventFormInputs, 'fanPollingEnabled'>,
  hasMatchups: boolean
): Pick<
  CreateTicketEventInput,
  'affiliateEnabled' | 'affiliateCommissionPercent' | 'fanPollingEnabled'
> => ({
  ...getEventAffiliateInput(values),
  fanPollingEnabled: hasMatchups && values.fanPollingEnabled,
});

type BuildCreateTicketEventInputOptions = {
  streamId?: string;
  coverImageFileKey?: string;
  matchups: CreateMatchupInput[];
};

export const buildCreateTicketEventInput = (
  values: EventFormInputs,
  { streamId, coverImageFileKey, matchups }: BuildCreateTicketEventInputOptions
): CreateTicketEventInput => {
  const ticketsEnabled = eventSellsTickets(values);

  return {
    ...(streamId && { streamId }),
    ...(coverImageFileKey && { coverImageFileKey }),
    ...getTicketEventBaseInput(values),
    // A stream-linked event follows the stream's repeat, card and affiliate program.
    ...(!streamId && {
      repeatsEvery: values.isRecurring ? values.repeatsEveryEnum.value : null,
      ...getStandaloneEventEngagementInput(values, matchups.length > 0),
    }),
    ticketsEnabled,
    ...(ticketsEnabled && {
      tiers: toTicketTierInputs(values.tiers),
    }),
    ...(!streamId && matchups.length > 0 && { matchups }),
  };
};

/** An event as the wizard's values, with its start as wall-clock time in its own zone. */
export const getEventFormValuesFromTicketEvent = (
  event: GetTicketEventForEdit_hostedTicketEvent,
  tiers: GetTicketEventForEdit_hostedTicketTiers[]
): EventFormInputs => {
  const timeZone = findVenueTimeZoneOption(event.timezone, event.startsAt);
  const start = toWallClockInZone(event.startsAt, timeZone.tzName);
  const venue: EventVenueDraft = {
    name: event.venueName,
    addressLine1: event.venueAddress.addressLine1,
    city: event.venueAddress.city,
    region: event.venueAddress.region,
    postalCode: event.venueAddress.postalCode,
    country: event.venueAddress.country,
    latitude: event.latitude,
    longitude: event.longitude,
    timezone: event.timezone,
  };
  const bouts = mapMatchupsToBouts(event.matchups);

  return {
    ...getEventFormDefaultValues(timeZone),
    name: event.name,
    description: event.description ?? '',
    hashtags: event.hashtags.map(({ name }) => name),
    previewImage: event.coverImageUrl ?? '',
    isRecurring: Boolean(event.repeatsEvery),
    repeatsEveryEnum:
      REPEAT_EVENT_OPTIONS.find(({ value }) => value === event.repeatsEvery) ??
      REPEAT_EVENT_OPTIONS[0],
    startDate: start,
    startTime: start,
    scheduleDate: start,
    salesCloseHours: getSalesCloseHours(event.startsAt, event.salesCloseAt),
    venue,
    venueSearch: getVenueSearchQuery(venue),
    tiers: tiers.map(({ id, label, price, capacity }) => ({
      id,
      label,
      price,
      capacity,
      isSaved: true,
    })),
    affiliateEnabled: event.affiliateEnabled,
    affiliateCommissionPercent:
      event.affiliateCommissionPercent === null
        ? ''
        : String(event.affiliateCommissionPercent),
    matchcardEnabled: bouts.length > 0,
    bouts,
    fanPollingEnabled: event.fanPollingEnabled,
  };
};

const latest = (...dates: string[]): string =>
  dates.reduce((later, date) => (dayjs(date).isAfter(later) ? date : later));

/**
 * Moves doors, end, admission and sales close with the start, so the event keeps its own timings, unless the host
 * picked a new sales close; an event runs at least until its sales close.
 */
export const getRescheduleTicketEventInput = (
  event: Pick<
    GetTicketEventForEdit_hostedTicketEvent,
    | 'id'
    | 'startsAt'
    | 'doorsOpenAt'
    | 'endsAt'
    | 'admissionClosesAt'
    | 'salesCloseAt'
  >,
  startsAt: string,
  salesCloseHours: number | null,
  expectedVersion: number
): RescheduleTicketEventInput => {
  const shiftMs = dayjs(startsAt).diff(event.startsAt);
  const shift = (date: string) =>
    dayjs(date).add(shiftMs, 'millisecond').toISOString();
  const salesCloseAt =
    salesCloseHours === null
      ? shift(event.salesCloseAt)
      : getTicketSalesCloseAt(startsAt, salesCloseHours);
  const admissionClosesAt = latest(
    shift(event.admissionClosesAt),
    salesCloseAt
  );

  return {
    ticketEventId: event.id,
    expectedVersion,
    startsAt,
    doorsOpenAt: shift(event.doorsOpenAt),
    endsAt: latest(shift(event.endsAt), admissionClosesAt),
    admissionClosesAt,
    salesCloseAt,
  };
};

type EventStatusFilterOption = {
  value: TicketEventStatus | null;
  label: string;
};

export const EVENT_STATUS_FILTER_OPTIONS: EventStatusFilterOption[] = [
  { value: null, label: 'All Statuses' },
  { value: TicketEventStatus.OnSale, label: 'Published' },
  { value: TicketEventStatus.Draft, label: 'Draft' },
  { value: TicketEventStatus.Paused, label: 'Paused' },
  { value: TicketEventStatus.Closed, label: 'Sales Closed' },
  { value: TicketEventStatus.Postponed, label: 'Postponed' },
  { value: TicketEventStatus.Cancelled, label: 'Canceled' },
];

export const getTicketEventStatusMeta = (
  event: Pick<HostedTicketEventFields, 'status' | 'ticketsEnabled'>
): { label: string; variant: StatusVariant } => {
  switch (event.status) {
    case TicketEventStatus.OnSale:
      return event.ticketsEnabled
        ? { label: 'On Sale', variant: 'approved' }
        : { label: 'Published', variant: 'approved' };
    case TicketEventStatus.Paused:
      return { label: 'Paused', variant: 'applied' };
    case TicketEventStatus.Closed:
      return { label: 'Sales Closed', variant: 'secondary' };
    case TicketEventStatus.Postponed:
      return { label: 'Postponed', variant: 'change-needed' };
    case TicketEventStatus.Cancelled:
      return { label: 'Canceled', variant: 'declined' };
    default:
      return { label: 'Draft', variant: 'disabled' };
  }
};

/** Ticket price first, then what the linked stream costs. */
export const getTicketEventPriceLabels = (
  event: Pick<HostedTicketEventFields, 'ticketPriceFrom' | 'stream'>
): string[] => {
  const labels: string[] = [];

  if (event.ticketPriceFrom !== null) {
    labels.push(`From ${formatCurrencyString(event.ticketPriceFrom)}`);
  }

  if (event.stream) {
    labels.push(
      event.stream.isFree
        ? 'Free stream'
        : `${formatCurrencyString(
            event.stream.requestedPrice ?? event.stream.price
          )} stream`
    );
  }

  return labels;
};

export type ManagedEventRow = {
  titleHref: string | null;
  dateLabel: string;
  primaryPrice: string;
  secondaryPrice?: string;
  status: { label: string; variant: StatusVariant };
  isCancelled: boolean;
};

/** A streamed event opens on its stream; any other once it is published and has a page. */
export const getHostedTicketEventHref = (
  event: Pick<
    HostedTicketEventFields,
    'slug' | 'status' | 'publishedAt' | 'stream'
  >
): string | null => {
  if (event.status === TicketEventStatus.Cancelled) {
    return null;
  }

  const hasPage = event.stream
    ? Boolean(event.stream.slug && event.stream.store?.slug)
    : Boolean(event.publishedAt);

  return hasPage ? getTicketEventPath(event) : null;
};

export const getTicketEventInviteLink = (
  ticketEventId: string,
  invite: string
): string => `${getEnvLink()}${EVENT_INVITE}/${ticketEventId}/${invite}`;

/** What a row of My Events shows, for the table and the phone card alike. */
export const getManagedEventRow = (
  event: HostedTicketEventFields
): ManagedEventRow => {
  const isCancelled = event.status === TicketEventStatus.Cancelled;
  const [primaryPrice, secondaryPrice] = getTicketEventPriceLabels(event);

  return {
    titleHref: getHostedTicketEventHref(event),
    dateLabel: formatTicketEventTableDate(event.startsAt, event.timezone),
    primaryPrice: primaryPrice || '—',
    secondaryPrice,
    status: getTicketEventStatusMeta(event),
    isCancelled,
  };
};

export type ManagedEventSort = {
  label: string;
  orderBy: HostedTicketEventsOrderBy;
  direction: SortDirection;
};

/** The table's sortable columns, as one list for the phone's sort picker. */
export const MANAGED_EVENT_SORTS: ManagedEventSort[] = [
  {
    label: 'Recently created',
    orderBy: HostedTicketEventsOrderBy.CreatedAt,
    direction: SortDirection.DESC,
  },
  {
    label: 'Date: soonest first',
    orderBy: HostedTicketEventsOrderBy.StartsAt,
    direction: SortDirection.ASC,
  },
  {
    label: 'Date: latest first',
    orderBy: HostedTicketEventsOrderBy.StartsAt,
    direction: SortDirection.DESC,
  },
  {
    label: 'Title A-Z',
    orderBy: HostedTicketEventsOrderBy.Name,
    direction: SortDirection.ASC,
  },
  {
    label: 'Title Z-A',
    orderBy: HostedTicketEventsOrderBy.Name,
    direction: SortDirection.DESC,
  },
  {
    label: 'Price: low to high',
    orderBy: HostedTicketEventsOrderBy.TicketPrice,
    direction: SortDirection.ASC,
  },
  {
    label: 'Price: high to low',
    orderBy: HostedTicketEventsOrderBy.TicketPrice,
    direction: SortDirection.DESC,
  },
];
